<template>
    <div class="container-fluid py-4">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">
                            <Users :size="20" class="me-2" />
                            Pending Registrations
                        </h5>
                        <button class="btn btn-sm btn-outline-primary" @click="loadRegistrations">
                            <RefreshCw :size="16" class="me-1" />
                            Refresh
                        </button>
                    </div>
                    <div class="card-body">
                        <div v-if="isLoading" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <div v-else-if="error" class="alert alert-danger">
                            {{ error }}
                        </div>

                        <div v-else-if="registrations.length === 0" class="text-center py-5 text-muted">
                            <Users :size="48" class="mb-3 opacity-50" />
                            <p>No pending registrations</p>
                        </div>

                        <div v-else class="table-responsive">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Domain</th>
                                        <th>Registered</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="reg in registrations" :key="reg.Id">
                                        <td>
                                            <strong>{{ reg.Name }}</strong>
                                        </td>
                                        <td>
                                            <a :href="'mailto:' + reg.Email">{{ reg.Email }}</a>
                                        </td>
                                        <td>{{ reg.Phone || '-' }}</td>
                                        <td>
                                            <span v-if="reg.Domain" class="badge bg-info">
                                                {{ reg.Domain }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                        <td>{{ formatDate(reg.RegisteredAt) }}</td>
                                        <td>
                                            <span 
                                                class="badge"
                                                :class="{
                                                    'bg-warning': reg.Status === 'Pending',
                                                    'bg-success': reg.Status === 'Approved',
                                                    'bg-danger': reg.Status === 'Rejected'
                                                }"
                                            >
                                                {{ reg.Status }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Api from '@/services/ApiService';
import { Users, RefreshCw } from 'lucide-vue-next';

interface Registration {
    Id: string;
    Name: string;
    Email: string;
    Phone?: string;
    Domain?: string;
    RegisteredAt: string;
    Status: string;
}

const registrations = ref<Registration[]>([]);
const isLoading = ref(true);
const error = ref('');

const loadRegistrations = async () => {
    isLoading.value = true;
    error.value = '';
    
    try {
        const res = await Api.getRegistrations();
        
        if (res && res.Status === 1) {
            registrations.value = res.Data || [];
        } else {
            error.value = res?.Message || 'Failed to load registrations';
        }
    } catch (err) {
        console.error('Error loading registrations:', err);
        error.value = 'An error occurred loading registrations';
    } finally {
        isLoading.value = false;
    }
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

onMounted(() => {
    loadRegistrations();
});
</script>

<style scoped>
.card {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>
